import React, { useState, useEffect } from "react";
import { Table, Loader, Button, Icon, Label } from "semantic-ui-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EnvironmentalRiskMatrixConesaform } from "../../../../api";
import { BasicModal, ConfirmModal } from "../../../Shared";
import { ConesaForm } from "../ConesaForm";
import { RISK_MATRIX_EXAMPLES, loadRiskMatrixExamples } from "../riskMatrixExamples";
import { downloadRiskMatrixReport } from "../riskMatrixReport";
import "../ListEnvironmentalRiskMatrixForms/ListEnvironmentalRiskMatrixForms.scss";

const environmentalRiskMatrixConesaCtrl = new EnvironmentalRiskMatrixConesaform();

const SIGNIFICANCE_COLOR = {
  Irrelevante: "grey",
  Moderado: "yellow",
  Severo: "orange",
  Crítico: "red",
};
const SIGNIFICANCE_KEY = {
  Irrelevante: "risk_matrix_conesa_sig_irrelevant",
  Moderado: "risk_matrix_conesa_sig_moderate",
  Severo: "risk_matrix_conesa_sig_severe",
  Crítico: "risk_matrix_conesa_sig_critical",
};

const CSV_COLUMNS = [
  "title", "projectAction", "environmentalFactor", "sign",
  "IN", "EX", "MO", "PE", "RV", "SI", "AC", "EF", "PR", "MC",
  "importance", "significance", "controls", "action",
];

function toCSV(items) {
  const escapeCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = CSV_COLUMNS.join(",");
  const rows = items.map((item) => CSV_COLUMNS.map((col) => escapeCell(item[col])).join(","));
  return [header, ...rows].join("\n");
}

function downloadCSV(items) {
  const csv = toCSV(items);
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `matriz_conesa_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function ConesaList({ siteSelected, reload, onReload, embedded, onOpenAnalysis }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [items, setItems] = useState(null);
  const [editItem, setEditItem] = useState(undefined);
  const [showForm, setShowForm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loadingExamples, setLoadingExamples] = useState(false);

  useEffect(() => {
    (async () => {
      if (!siteSelected) {
        setItems([]);
        return;
      }
      try {
        const response = await environmentalRiskMatrixConesaCtrl.getEnvironmentalRiskMatricesConesaBySite(accessToken, siteSelected);
        setItems(response.code === 200 ? response.riskMatrices || [] : []);
      } catch (error) {
        console.error(error);
        setItems([]);
      }
    })();
  }, [siteSelected, reload, accessToken]);

  const openCreate = () => {
    setEditItem(undefined);
    setShowForm(true);
  };

  const handleLoadExamples = async () => {
    setLoadingExamples(true);
    try {
      await loadRiskMatrixExamples({
        accessToken,
        siteSelected,
        examples: RISK_MATRIX_EXAMPLES.conesa,
        createFn: (token, payload) => environmentalRiskMatrixConesaCtrl.createEnvironmentalRiskMatrixConesa(token, payload),
      });
      toast.success(t("risk_matrix_examples_loaded"));
      onReload();
    } catch (error) {
      toast.error(error?.msg || error?.message || t("server_connection_error"));
    } finally {
      setLoadingExamples(false);
    }
  };
  const openEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const res = await environmentalRiskMatrixConesaCtrl.deleteEnvironmentalRiskMatrixConesa(accessToken, pendingDelete._id);
      if (res.code === 200) {
        toast.success(t("risk_matrix_item_deleted"));
        onReload();
      }
    } catch (error) {
      toast.error(error?.msg || error?.message || t("server_connection_error"));
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  if (!items) return <Loader active inline="centered" />;

  const severeCount = items.filter((i) => i.importance >= 50).length;
  const opportunitiesCount = items.filter((i) => i.sign === "Positivo (+)").length;
  const sortedByImportance = items.slice().sort((a, b) => b.importance - a.importance);

  const handleDownloadReport = () => {
    if (items.length === 0) {
      toast.info(`${t("risk_matrix_empty_in")} ${t("risk_matrix_methodology_conesa")}.`);
      return;
    }
    downloadRiskMatrixReport({
      t,
      filenamePrefix: "conesa",
      tag: t("risk_matrix_methodology_conesa"),
      title: t("risk_matrix_methodology_conesa_desc"),
      intro: t("risk_matrix_methodology_conesa_intro"),
      statCount: items.length,
      statLabel: t("risk_matrix_conesa_stat_impacts"),
      critCount: severeCount,
      critLabel: t("risk_matrix_conesa_stat_severe"),
      oppCount: opportunitiesCount,
      oppLabel: opportunitiesCount === 1 ? t("risk_matrix_stat_opportunity") : t("risk_matrix_stat_opportunities"),
      columns: [
        { key: "title", label: t("title") },
        { key: "projectAction", label: t("risk_matrix_eia_project_action") },
        { key: "environmentalFactor", label: t("risk_matrix_eia_environmental_factor") },
        { key: "sign", label: t("risk_matrix_gri_sign") },
        { key: "importance", label: t("risk_matrix_conesa_importance") },
        { key: "significance", label: t("risk_matrix_significance") },
      ],
      sortedItems: sortedByImportance,
      items,
      hasHeatMap: false,
    });
  };

  return (
    <div className="risk-matrix-list">
      {!embedded && (
        <>
          <div className="risk-matrix-list__head">
            <div>
              <span className="risk-matrix-list__tag">{t("risk_matrix_methodology_conesa")}</span>
              <h2>{t("risk_matrix_methodology_conesa_desc")}</h2>
            </div>
            <div className="risk-matrix-list__head-actions">
              <Button className="risk-matrix-list__btn risk-matrix-list__btn--solid" onClick={handleDownloadReport}>
                <Icon name="file alternate outline" /> {t("risk_matrix_download_report")}
              </Button>
              {onOpenAnalysis && (
                <Button className="risk-matrix-list__btn risk-matrix-list__btn--outline" onClick={onOpenAnalysis}>
                  {t("risk_matrix_open_analysis")} →
                </Button>
              )}
            </div>
          </div>
          <p className="risk-matrix-list__intro">{t("risk_matrix_methodology_conesa_intro")}</p>
        </>
      )}

      {embedded && items.length > 0 && (
        <div className="risk-matrix-list__toolbar">
          <Button className="risk-matrix-list__btn risk-matrix-list__btn--solid" onClick={openCreate}>
            <Icon name="plus" /> {t("risk_matrix_new_item")}
          </Button>
          <Button className="risk-matrix-list__btn risk-matrix-list__btn--outline" onClick={() => downloadCSV(items)}>
            <Icon name="download" /> {t("risk_matrix_export_csv")}
          </Button>
        </div>
      )}

      {items.length > 0 && (
        <div className="risk-matrix-list__stats">
          <span className="risk-matrix-list__stat">
            <b>{items.length}</b> {t("risk_matrix_conesa_stat_impacts")}
          </span>
          <span className="risk-matrix-list__stat">
            <b>{severeCount}</b> {t("risk_matrix_conesa_stat_severe")}
          </span>
          <span className="risk-matrix-list__stat">
            <b>{opportunitiesCount}</b>{" "}
            {opportunitiesCount === 1 ? t("risk_matrix_stat_opportunity") : t("risk_matrix_stat_opportunities")}
          </span>
        </div>
      )}

      {items.length === 0 ? (
        <div className="risk-matrix-list__empty-state">
          <p>
            {t("risk_matrix_empty_in")} {t("risk_matrix_methodology_conesa")}.
          </p>
          <div className="risk-matrix-list__empty-actions">
            <Button className="risk-matrix-list__btn risk-matrix-list__btn--solid" onClick={openCreate}>
              {t("risk_matrix_empty_open_first")} →
            </Button>
            <Button
              className="risk-matrix-list__btn risk-matrix-list__btn--outline"
              loading={loadingExamples}
              disabled={loadingExamples}
              onClick={handleLoadExamples}
            >
              {t("risk_matrix_load_examples")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="risk-matrix-list__scrollx">
          <Table celled sortable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>{t("title")}</Table.HeaderCell>
                <Table.HeaderCell>{t("risk_matrix_eia_project_action")}</Table.HeaderCell>
                <Table.HeaderCell>{t("risk_matrix_eia_environmental_factor")}</Table.HeaderCell>
                <Table.HeaderCell>{t("risk_matrix_gri_sign")}</Table.HeaderCell>
                <Table.HeaderCell>{t("risk_matrix_conesa_importance")}</Table.HeaderCell>
                <Table.HeaderCell>{t("risk_matrix_significance")}</Table.HeaderCell>
                <Table.HeaderCell>{t("actions")}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((item) => (
                <Table.Row key={item._id}>
                  <Table.Cell>{item.title}</Table.Cell>
                  <Table.Cell>{item.projectAction}</Table.Cell>
                  <Table.Cell>{item.environmentalFactor}</Table.Cell>
                  <Table.Cell>
                    {item.sign === "Positivo (+)" ? t("risk_matrix_gri_sign_positive") : t("risk_matrix_gri_sign_negative")}
                  </Table.Cell>
                  <Table.Cell>{item.importance}</Table.Cell>
                  <Table.Cell>
                    <Label color={SIGNIFICANCE_COLOR[item.significance] || "grey"}>
                      {t(SIGNIFICANCE_KEY[item.significance]) || item.significance}
                    </Label>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="risk-matrix-list__row-actions">
                      <Button
                        icon
                        size="small"
                        className="risk-matrix-list__row-btn risk-matrix-list__row-btn--edit"
                        onClick={() => openEdit(item)}
                        title={t("edit")}
                      >
                        <Icon name="pencil" />
                      </Button>
                      <Button
                        icon
                        size="small"
                        className="risk-matrix-list__row-btn risk-matrix-list__row-btn--delete"
                        onClick={() => setPendingDelete(item)}
                        title={t("delete")}
                      >
                        <Icon name="trash" />
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      <BasicModal show={showForm} close={() => setShowForm(false)} size="small" className="risk-matrix-modal">
        <ConesaForm item={editItem} siteSelected={siteSelected} onClose={() => setShowForm(false)} onReload={onReload} />
      </BasicModal>

      <ConfirmModal
        open={!!pendingDelete}
        title={t("risk_matrix_delete_confirm_title")}
        content={pendingDelete?.title}
        loading={deleting}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
